import _ from "lodash";
import { call, put, select, takeLatest } from "redux-saga/effects";
import { setPersonEffectiveDate, setPersonPasswordReset } from "../actions";
import { Auth } from "../utilities/auth";
import axios from "../utilities/axiosWrapper";

// Auth reducer
export const selectAuthReducer = (state: any) => state.authReducer;

/**
 * @desc The axios wrapper returns the error object instead of throwing, so on a
 * non 2xx the status and body sit under `response`.
 * @param {*} jsonResponse
 * @return {*} {{ status: number, body: any }}
 */
const readResponse = (jsonResponse: any): { status: number; body: any } => ({
  status: jsonResponse?.status ?? jsonResponse?.response?.status ?? 0,
  body: jsonResponse?.data ?? jsonResponse?.response?.data ?? {},
});

/**
 * @param {*} body
 * @return {string} the API's message, or a generic fallback
 */
const readErrorMessage = (body: any): string =>
  body?.error?.message ?? "Something went wrong. Please try again.";

/**
 * @desc Read a plan member's effective date and whether it may be changed.
 * @param {*} action
 * @return {*}  {Generator}
 */
export function* fetchPersonEffectiveDateWorker(action: any): Generator {
  let selectedAuthReducer: any = yield select(selectAuthReducer);
  let params: any = {
    method: "get",
    path: `persons/${action.data}/effective-date?at=${selectedAuthReducer.accessToken}`,
    data: {},
  };

  try {
    const apikeys: any = yield Auth.getAuthTokens();
    const jsonResponse: any = yield call(axios, params, apikeys);
    const { status, body } = readResponse(jsonResponse);

    if (status >= 200 && status < 300) {
      return yield put(
        setPersonEffectiveDate({
          effectiveDate: _.get(body, "data", null),
          effectiveDateLoading: false,
        })
      );
    }
    return yield put(
      setPersonEffectiveDate({
        effectiveDateLoading: false,
        effectiveDateError: readErrorMessage(body),
      })
    );
  } catch (ex) {
    return yield put(
      setPersonEffectiveDate({
        effectiveDateLoading: false,
        effectiveDateError: "Could not read the effective date.",
      })
    );
  }
}

/**
 * @desc Change a plan member's effective date.
 * @param {*} action
 * @return {*}  {Generator}
 */
export function* updatePersonEffectiveDateWorker(action: any): Generator {
  let selectedAuthReducer: any = yield select(selectAuthReducer);
  let params: any = {
    method: "post",
    path: `persons/${action.data.personGuid}/effective-date?at=${selectedAuthReducer.accessToken}`,
    data: { effectiveDate: action.data.effectiveDate },
  };

  try {
    const apikeys: any = yield Auth.getAuthTokens();
    const jsonResponse: any = yield call(axios, params, apikeys);
    const { status, body } = readResponse(jsonResponse);

    if (status >= 200 && status < 300) {
      return yield put(
        setPersonEffectiveDate({
          effectiveDate: _.get(body, "data", null),
          effectiveDateSaving: false,
          effectiveDateSaved: true,
        })
      );
    }
    return yield put(
      setPersonEffectiveDate({
        effectiveDateSaving: false,
        effectiveDateError: readErrorMessage(body),
      })
    );
  } catch (ex) {
    return yield put(
      setPersonEffectiveDate({
        effectiveDateSaving: false,
        effectiveDateError: "The effective date could not be changed.",
      })
    );
  }
}

/**
 * @desc Send a person a new temporary password.
 * @param {*} action
 * @return {*}  {Generator}
 */
export function* resetPersonPasswordWorker(action: any): Generator {
  let selectedAuthReducer: any = yield select(selectAuthReducer);
  let params: any = {
    method: "post",
    path: `persons/${action.data}/password-reset?at=${selectedAuthReducer.accessToken}`,
    data: {},
  };

  try {
    const apikeys: any = yield Auth.getAuthTokens();
    const jsonResponse: any = yield call(axios, params, apikeys);
    const { status, body } = readResponse(jsonResponse);

    if (status >= 200 && status < 300) {
      return yield put(
        setPersonPasswordReset({
          passwordResetSending: false,
          passwordResetSentTo: _.get(body, "data.email", ""),
        })
      );
    }
    return yield put(
      setPersonPasswordReset({
        passwordResetSending: false,
        passwordResetError: readErrorMessage(body),
      })
    );
  } catch (ex) {
    return yield put(
      setPersonPasswordReset({
        passwordResetSending: false,
        passwordResetError: "The password could not be reset.",
      })
    );
  }
}

/**
 * @return {*}  {Generator}
 */
function* fetchPersonEffectiveDateWatcher(): Generator {
  yield takeLatest("FETCH_PERSON_EFFECTIVE_DATE", fetchPersonEffectiveDateWorker);
}

/**
 * @return {*}  {Generator}
 */
function* updatePersonEffectiveDateWatcher(): Generator {
  yield takeLatest("UPDATE_PERSON_EFFECTIVE_DATE", updatePersonEffectiveDateWorker);
}

/**
 * @return {*}  {Generator}
 */
function* resetPersonPasswordWatcher(): Generator {
  yield takeLatest("RESET_PERSON_PASSWORD", resetPersonPasswordWorker);
}

export {
  fetchPersonEffectiveDateWatcher,
  updatePersonEffectiveDateWatcher,
  resetPersonPasswordWatcher,
};
